import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { GroupLogo } from "@/components/GroupLogo";
import { createGroup, requestJoinGroup, loadMyGroups, lookupGroup, loadMyPendingRequests } from "@/lib/groups";
import { checkGroupNameAvailable } from "@/lib/bonus";
import { loadTopGroups } from "@/lib/ranking";
import { InviteButton } from "@/components/InviteButton";
import { Plus, Users, Trophy, Clock } from "lucide-react";

export default function Groups() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialCode = (params.get("code") || "").toUpperCase().slice(0, 6);
  const [mode, setMode] = useState<"list" | "join" | "create">(initialCode.length === 6 ? "join" : "list");
  const [code, setCode] = useState(initialCode);
  const [preview, setPreview] = useState<any>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [topGroups, setTopGroups] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [nameStatus, setNameStatus] = useState<"idle" | "checking" | "ok" | "taken">("idle");

  useEffect(() => {
    const trimmed = name.trim();
    if (trimmed.length < 2) { setNameStatus("idle"); return; }
    setNameStatus("checking");
    const t = setTimeout(async () => {
      const ok = await checkGroupNameAvailable(trimmed);
      setNameStatus(ok ? "ok" : "taken");
    }, 350);
    return () => clearTimeout(t);
  }, [name]);

  useEffect(() => { document.title = "Tippgruppen – Englputzeder Tippspiel"; }, []);

  const reload = async () => {
    if (!user) return;
    const [mine, top, pending] = await Promise.all([
      loadMyGroups(user.id),
      loadTopGroups(5),
      loadMyPendingRequests(user.id),
    ]);
    setGroups(mine);
    setTopGroups(top);
    setPendingRequests(pending);
  };
  useEffect(() => { reload(); }, [user]);

  useEffect(() => {
    setPreview(null);
    if (code.length !== 6) return;
    const t = setTimeout(async () => {
      const found = await lookupGroup(code);
      setPreview(found);
    }, 300);
    return () => clearTimeout(t);
  }, [code]);

  const submitJoin = async () => {
    setBusy(true);
    try {
      const res = await requestJoinGroup(code);
      if (res.status === "already_member") {
        toast.info(`Du bist bereits Mitglied von „${res.group_name}"`);
      } else {
        toast.success(`⏳ Anfrage an „${res.group_name}" gesendet — ein Admin muss dich bestätigen.`);
      }
      setMode("list"); setCode(""); reload();
    }
    catch (e: any) { toast.error(e.message); }
    setBusy(false);
  };
  const submitCreate = async () => {
    setBusy(true);
    try { await createGroup(name.trim()); toast.success(`Gruppe „${name}" erstellt 🚀`); setMode("list"); setName(""); reload(); }
    catch (e: any) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <h1 className="font-display font-extrabold text-3xl flex items-center gap-2"><Users className="h-7 w-7" /> Tippgruppen</h1>

      {mode === "list" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setMode("join")} className="h-14 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">🎟️ Code eingeben</Button>
            <Button onClick={() => setMode("create")} variant="outline" className="h-14 font-bold"><Plus className="h-4 w-4 mr-1" /> Neu erstellen</Button>
          </div>
          <div className="space-y-2">
            {groups.length === 0 && <div className="bg-card rounded-lg shadow-card p-6 text-center text-sm text-muted-foreground">Du bist noch in keiner Gruppe.</div>}
            {groups.map(g => (
              <div key={g.id} className="bg-card rounded-lg shadow-card p-3 flex items-center gap-3">
                <Link to={`/gruppe/${g.slug}`} className="flex items-center gap-3 flex-1 min-w-0 tap">
                  <GroupLogo url={g.logo_url} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{g.name}</div>
                    <div className="text-[11px] text-muted-foreground">{g.member_count} Mitglieder · {g.my_role === "admin" ? "👑 Admin" : "Mitglied"}</div>
                  </div>
                </Link>
                <div className="flex flex-col items-end gap-1">
                  <div className="text-[10px] text-muted-foreground">Code <span className="font-mono font-bold">{g.invite_code}</span></div>
                  <InviteButton variant="icon" groupName={g.name} code={g.invite_code} />
                </div>
              </div>
            ))}
          </div>

          {pendingRequests.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-1">
                <Clock className="h-3.5 w-3.5" /> Offene Anfragen
              </div>
              <div className="bg-card rounded-lg shadow-card overflow-hidden">
                {pendingRequests.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0">
                    <GroupLogo url={r.group?.logo_url} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{r.group?.name ?? "Gruppe"}</div>
                      <div className="text-[10px] text-muted-foreground">Wartet auf Freigabe durch Admin</div>
                    </div>
                    <div className="inline-flex items-center gap-1 bg-muted text-muted-foreground rounded-pill px-2 py-0.5 text-[10px] font-bold">
                      <Clock className="h-3 w-3" /> Pending
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {topGroups.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-1">
                <Trophy className="h-3.5 w-3.5 text-accent-foreground" /> Größte Tippgruppen
              </div>
              <div className="bg-card rounded-lg shadow-card overflow-hidden">
                {topGroups.map((g, i) => (
                  <Link
                    key={g.id}
                    to={`/gruppe/${g.slug}`}
                    className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 tap"
                  >
                    <div className="w-14 shrink-0 text-center font-display font-extrabold text-xs tabular-nums text-muted-foreground">#{i + 1}</div>
                    <GroupLogo url={g.logo_url} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{g.name}</div>
                      <div className="text-[10px] text-muted-foreground">{g.member_count} Mitglieder</div>
                    </div>
                    <div className="inline-flex items-center gap-1 bg-accent/20 text-accent-foreground rounded-pill px-2 py-0.5 text-[11px] font-bold tabular-nums">
                      <Users className="h-3 w-3" /> {g.member_count}
                    </div>
                  </Link>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground px-1">Tritt einer großen Liga mit Code bei oder gründe deine eigene.</p>
            </div>
          )}
        </>
      )}

      {mode === "join" && (
        <div className="bg-card rounded-lg shadow-card p-4 space-y-3">
          <h2 className="font-display font-bold text-lg">Gruppe beitreten</h2>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,6))} placeholder="z.B. K7M2X9" className="h-14 text-2xl font-mono font-bold tracking-[0.3em] text-center" maxLength={6} />
          {preview && (
            <div className="rounded-md bg-accent/20 border-2 border-accent px-3 py-3 text-sm flex items-center gap-3">
              <GroupLogo url={preview.logo_url} size={40} />
              <div>
                <div className="font-bold">{preview.name}</div>
                <div className="text-xs text-muted-foreground">{preview.member_count} Mitglieder</div>
              </div>
            </div>
          )}
          <div className="rounded-md bg-muted/60 px-3 py-2 text-[11px] text-muted-foreground flex items-start gap-2">
            <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Dein Beitritt muss von einem Gruppen-Admin bestätigt werden.</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setMode("list"); setCode(""); }} className="flex-1">Abbrechen</Button>
            <Button onClick={submitJoin} disabled={busy || !preview} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">{busy ? "…" : "Anfrage senden"}</Button>
          </div>
        </div>
      )}

      {mode === "create" && (
        <div className="bg-card rounded-lg shadow-card p-4 space-y-3">
          <h2 className="font-display font-bold text-lg">Neue Gruppe</h2>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gruppenname" className="h-12" maxLength={50} />
          {name.trim().length >= 2 && (
            nameStatus === "checking" ? <p className="text-xs text-muted-foreground">Prüfe Verfügbarkeit …</p>
            : nameStatus === "taken" ? <p className="text-xs text-destructive">❌ Gruppenname bereits vergeben</p>
            : nameStatus === "ok" ? <p className="text-xs text-success">✅ Verfügbar</p>
            : null
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setMode("list"); setName(""); }} className="flex-1">Abbrechen</Button>
            <Button onClick={submitCreate} disabled={busy || name.trim().length < 2 || nameStatus !== "ok"} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">{busy ? "…" : "Erstellen"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
